import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

interface IBrokenRefReportItem {
    type: string;
    ref_name: string;
    group: string;
    count?: number;
    message: string;
    can_auto_fix: boolean;
    source?: string;
    page?: string;
    pages?: string[];
    transclusion?: string;
    reason?: string;
    revision_id?: number | string;
    revision_url?: string;
    timestamp?: string;
    searched_revisions?: number;
    introduced_revision_id?: number | string;
    introduced_parent_id?: number | string;
    introduced_user?: string;
    introduced_timestamp?: string;
    introduced_diff_url?: string;
}

export interface IBrokenRefReport {
    category?: string;
    fixes?: IBrokenRefReportItem[];
    suggestions?: IBrokenRefReportItem[];
    unresolved?: IBrokenRefReportItem[];
    ambiguous?: IBrokenRefReportItem[];
    undefined_names?: string[];
}

interface IBrokenRefReportProps {
    report?: IBrokenRefReport | null;
}

function hasItems(items?: IBrokenRefReportItem[]) {
    return Array.isArray(items) && items.length > 0;
}

function sectionCount(report: IBrokenRefReport) {
    return (report.fixes?.length || 0) +
        (report.suggestions?.length || 0) +
        (report.unresolved?.length || 0) +
        (report.ambiguous?.length || 0);
}

function itemLabel(item: IBrokenRefReportItem) {
    const group = item.group ? item.group : "default";
    return `${item.ref_name} (${group})`;
}

function readableMeta(value: string) {
    return value.replace(/_/g, " ");
}

function sourceLabel(source: string) {
    if (source === "revision_history") {
        return "searched revision history";
    }
    return readableMeta(source);
}

function wikiBaseFromDiffUrl(diffUrl?: string) {
    if (!diffUrl) {
        return "";
    }
    try {
        const url = new URL(diffUrl);
        return url.origin;
    } catch {
        return "";
    }
}

function userPageUrl(item: IBrokenRefReportItem) {
    const base = wikiBaseFromDiffUrl(item.introduced_diff_url);
    if (!base || !item.introduced_user) {
        return "";
    }
    return `${base}/wiki/User:${encodeURIComponent(item.introduced_user.replace(/ /g, "_"))}`;
}

function renderSecondary(item: IBrokenRefReportItem) {
    const introMarker = " The broken reference first appears in ";
    const message = item.message.includes(introMarker) ?
        item.message.split(introMarker)[0] :
        item.message;
    const userUrl = userPageUrl(item);

    if (!item.introduced_revision_id && !item.introduced_user && !item.introduced_timestamp) {
        return message;
    }

    return (
        <Box component="span">
            <span>{message} </span>
            <span>The broken reference first appears in </span>
            {item.introduced_revision_id && item.introduced_diff_url ? (
                <Link href={item.introduced_diff_url} target="_blank" rel="noreferrer">
                    revision {item.introduced_revision_id}
                </Link>
            ) : (
                item.introduced_revision_id && <span>revision {item.introduced_revision_id}</span>
            )}
            {item.introduced_user && (
                <>
                    <span> by </span>
                    {userUrl ? (
                        <Link href={userUrl} target="_blank" rel="noreferrer">
                            {item.introduced_user}
                        </Link>
                    ) : (
                        <span>{item.introduced_user}</span>
                    )}
                </>
            )}
            {item.introduced_timestamp && <span> at {item.introduced_timestamp}</span>}
            <span>.</span>
        </Box>
    );
}

function renderItems(title: string, items: IBrokenRefReportItem[] | undefined, color: "success" | "info" | "warning" | "error") {
    if (!hasItems(items)) {
        return null;
    }

    return (
        <Box sx={{ marginTop: "14px" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
            <List dense disablePadding>
                {items!.map((item, index) => (
                    <ListItem key={`${item.type}-${item.ref_name}-${index}`} disableGutters alignItems="flex-start">
                        <ListItemText
                            primary={
                                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                    <Chip size="small" color={color} label={itemLabel(item)} />
                                    {item.count && item.count > 1 && (
                                        <Chip size="small" variant="outlined" label={`${item.count} uses`} />
                                    )}
                                    {item.source && (
                                        <Chip size="small" variant="outlined" label={sourceLabel(item.source)} />
                                    )}
                                    {item.page && (
                                        <Chip size="small" variant="outlined" label={item.page} />
                                    )}
                                    {item.pages && item.pages.length > 0 && (
                                        <Chip size="small" variant="outlined" label={`${item.pages.length} pages`} />
                                    )}
                                    {item.reason && (
                                        <Chip size="small" variant="outlined" label={readableMeta(item.reason)} />
                                    )}
                                    {item.revision_id && (
                                        <Chip size="small" variant="outlined" label={`rev ${item.revision_id}`} />
                                    )}
                                    {item.timestamp && (
                                        <Chip size="small" variant="outlined" label={item.timestamp} />
                                    )}
                                    {item.searched_revisions && (
                                        <Chip size="small" variant="outlined" label={`${item.searched_revisions} revisions searched`} />
                                    )}
                                    {item.introduced_diff_url && (
                                        <Chip
                                            size="small"
                                            label="View Diff That Introduced Error"
                                            component="a"
                                            href={item.introduced_diff_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            clickable
                                            sx={{
                                                backgroundColor: "primary.main",
                                                color: "primary.contrastText",
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                "&:hover": {
                                                    backgroundColor: "primary.dark"
                                                }
                                            }}
                                        />
                                    )}
                                    {item.transclusion && (
                                        <Chip size="small" variant="outlined" label="transclusion" />
                                    )}
                                </Box>
                            }
                            secondary={renderSecondary(item)}
                            secondaryTypographyProps={{ component: "div" }}
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
}

function BrokenRefReport({ report }: IBrokenRefReportProps) {
    if (!report || report.category !== "broken_ref" || sectionCount(report) === 0) {
        return null;
    }

    const fixed = report.fixes?.length || 0;
    const suggestions = report.suggestions?.length || 0;
    const unresolved = report.unresolved?.length || 0;
    const ambiguous = report.ambiguous?.length || 0;

    return (
        <Alert severity={fixed > 0 ? "info" : "warning"} sx={{ marginTop: "16px" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Broken reference analysis
            </Typography>
            <Typography variant="body2">
                {fixed} automatic fix{fixed === 1 ? "" : "es"}, {suggestions} suggestion{suggestions === 1 ? "" : "s"}, {unresolved} unresolved issue{unresolved === 1 ? "" : "s"}, {ambiguous} ambiguous case{ambiguous === 1 ? "" : "s"}.
            </Typography>
            {renderItems("Automatic fixes", report.fixes, "success")}
            {renderItems("Suggestions", report.suggestions, "info")}
            {renderItems("Unresolved", report.unresolved, "warning")}
            {renderItems("Ambiguous", report.ambiguous, "error")}
        </Alert>
    );
}

export default BrokenRefReport;
